import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApis } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import DiscountTaxManager from '../components/DiscountTaxManager';

const EstimateDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { estimateApis, orderApis } = useApis();
  const { user } = useAuth();

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingOrder, setCreatingOrder] = useState(false);
  
  // Pricing state
  const [customerPrice, setCustomerPrice] = useState(0); // What dealer pays to admin
  const [customerProfit, setCustomerProfit] = useState(0); // Dealer's markup percentage

  const fetchEstimate = useCallback(async () => {
    if (!estimateApis || !id) return;
    
    console.log('🔍 EstimateDetails: Loading estimate', id);
    try {
      setLoading(true);
      const response = await estimateApis.getEstimate(id);
      console.log('📦 EstimateDetails: Estimate loaded:', response);
      
      if (response && response.id) {
        setEstimate(response);
        calculatePrices(response);
      } else {
        console.log('❌ EstimateDetails: Estimate not found');
      }
    } catch (error) {
      console.error('❌ EstimateDetails: Error loading estimate:', error);
      alert('Error loading estimate: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [estimateApis, id]);

  const calculatePrices = (estimateData) => {
    if (!estimateData || !estimateData.estimateData) return;

    const data = estimateData.estimateData;
    const newCustomerPrice = data.total_price || 0;
    const newCustomerProfit = estimateData.estimate_profit || 0;
    
    setCustomerPrice(newCustomerPrice);
    setCustomerProfit(newCustomerProfit);
  };

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const handleEdit = () => {
    if (!estimate) return;
    console.log('✏️ EstimateDetails: Navigating to edit estimate', estimate.id);
    navigate(`/edit-estimate/${estimate.id}`);
  };

  const handleCreateOrder = async () => {
    if (!estimate || !orderApis || creatingOrder) return;

    const orderTag = estimate.estimateData?.customer?.customer_name || estimate.customerName || 'this order';
    const totalCost = (() => {
      const discountAmount = estimate?.estimateData?.discount_type === 'percentage'
        ? customerPrice * (estimate.estimateData.discount / 100)
        : estimate?.estimateData?.discount || 0;
      const subtotalAfterDiscount = customerPrice - discountAmount;
      const taxAmount = estimate?.estimateData?.tax > 0
        ? subtotalAfterDiscount * (estimate.estimateData.tax / 100)
        : 0;
      return subtotalAfterDiscount + taxAmount;
    })();
    const confirmMessage = `Are you sure you want to create order: "${orderTag}"?\n\nTotal Cost: $${totalCost.toFixed(2)}`;
    
    if (window.confirm(confirmMessage)) {
      try {
        setCreatingOrder(true);
        console.log('📦 EstimateDetails: Creating order for estimate ID:', estimate.id);
        console.log('📦 EstimateDetails: Current estimate status:', estimate.status);
        console.log('📦 EstimateDetails: Current isConvertedToOrder:', estimate.isConvertedToOrder);
        await estimateApis.saveEstimate(
          0, // paymentReceived - default to 0
          estimate.notes?.customer_note || "",
          estimate.notes?.terms_and_conditions || "",
          estimate.cartID,
          estimate.customerID,
          estimate.id,
          customerProfit,
          estimate.notes?.order_note || ""
        );

        console.log('✅ EstimateDetails: Order created successfully');
        
        alert('Order created successfully!');
        
        console.log('🔄 EstimateDetails: Refreshing estimate data...');
        await fetchEstimate(); // Refresh to show updated status
        console.log('✅ EstimateDetails: Estimate data refreshed');
        
      } catch (error) {
        console.error('❌ EstimateDetails: Error creating order:', error);
        alert('Error creating order: ' + error.message);
      } finally {
        setCreatingOrder(false);
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading estimate details...</p>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estimate Not Found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
            <div className="flex space-x-3">
              {!estimate.isConvertedToOrder && estimate.status !== "saved" && (
                <button
                  onClick={handleEdit}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700 px-4 py-2"
              >
                Back
              </button>
            </div>
          </div>

          {/* Order Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order No.</label>
              <p className="text-lg font-semibold">#{String(estimate.estimateNo || 0).padStart(3, '0')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Date</label>
              <p className="text-lg">{formatDate(estimate.createdAt)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
              <p className="text-lg">{formatDate(estimate.validUntil)}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="mt-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              estimate.isConvertedToOrder 
                ? 'bg-green-100 text-green-800' 
                : estimate.status === 'saved'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {estimate.isConvertedToOrder ? 'Converted to Order' : 
               estimate.status === 'saved' ? 'Active Order' : 'Draft'}
            </span>
          </div>
        </div>

        {/* Order Tag */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Tag</label>
            <p className="text-lg font-medium text-blue-600">
              {estimate.estimateData?.customer?.customer_name || estimate.customerName || 'No order tag specified'}
            </p>
          </div>
          {estimate.notes?.order_note && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Ship To</label>
              <p className="text-gray-900 whitespace-pre-wrap break-words">{estimate.notes.order_note}</p>
            </div>
          )}
        </div>

        {/* Products */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Products</h2>
          {estimate.estimateData?.products && estimate.estimateData.products.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {estimate.estimateData.products.map((product, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {product.product?.name || 'Product'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {product.status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product.user_input?.quantity || 1}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No products added</p>
          )}
        </div>

        {/* Discount & Tax Section - Only show for draft estimates */}
        {/* {estimate && estimate.status === 'draft' && estimate.estimateData && (
          <DiscountTaxManager
            estimate={estimate}
            estimateApis={estimateApis}
            onRefresh={fetchEstimate}
          />
        )} */}

        {/* Pricing */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Summary</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subtotal (Your Cost)</span>
              <span className="font-medium text-lg">${customerPrice.toFixed(2)}</span>
            </div>
            
            {/* Show discount if applied */}
            {estimate?.estimateData?.discount > 0 && (
              <div className="flex justify-between items-center text-green-600">
                <span>Discount ({estimate.estimateData.discount_type === 'percentage' ? `${estimate.estimateData.discount}%` : `$${estimate.estimateData.discount}`})</span>
                <span>-${(estimate.estimateData.discount_type === 'percentage' 
                  ? customerPrice * (estimate.estimateData.discount / 100) 
                  : estimate.estimateData.discount).toFixed(2)}</span>
              </div>
            )}
            
            {/* Show tax if applied */}
            {estimate?.estimateData?.tax > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tax ({estimate.estimateData.tax}%)</span>
                <span>+${((customerPrice - (estimate.estimateData.discount_type === 'percentage' 
                  ? customerPrice * (estimate.estimateData.discount / 100) 
                  : estimate.estimateData.discount || 0)) * (estimate.estimateData.tax / 100)).toFixed(2)}</span>
              </div>
            )}
            
            {/* Total Cost (what dealer pays to store) - always show */}
            <div className="flex justify-between items-center py-3 bg-green-50 px-4 rounded-lg border-l-4 border-green-500">
              <span className="font-medium text-gray-900">
                Total Cost (will be paid to {user?.storeData?.store_name || 'Store'})
              </span>
              <span className="font-semibold text-lg text-green-700">
                ${(() => {
                  const discountAmount = estimate?.estimateData?.discount_type === 'percentage'
                    ? customerPrice * (estimate.estimateData.discount / 100)
                    : estimate.estimateData.discount || 0;
                  const subtotalAfterDiscount = customerPrice - discountAmount;
                  const taxAmount = estimate?.estimateData?.tax > 0
                    ? subtotalAfterDiscount * (estimate.estimateData.tax / 100)
                    : 0;
                  return (subtotalAfterDiscount + taxAmount).toFixed(2);
                })()}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {/* {(estimate.notes?.customer_note || estimate.notes?.terms_and_conditions) && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
            <div className="space-y-4">
              {estimate.notes?.customer_note && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Note</label>
                  <p className="text-gray-900">{estimate.notes.customer_note}</p>
                </div>
              )}
              {estimate.notes?.terms_and_conditions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                  <p className="text-gray-900">{estimate.notes.terms_and_conditions}</p>
                </div>
              )}
            </div>
          </div>
        )} */}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          {estimate.isConvertedToOrder || estimate.status === "saved" ? (
            <></>
          ) : (
            <button
              onClick={handleCreateOrder}
              disabled={creatingOrder}
              className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingOrder ? 'Creating Order...' : 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimateDetails; 